import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { faEdit } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TwoColumn } from 'components/layout'
import { Spinner } from 'components/loaders'
import { useFacilities } from '@hooks/useFacilities'
import { useModal } from '@hooks/useModal'
import { useUserAdminRoles } from '@hooks/useUserAdminRoles'
import { AdminRolesForm } from './AdminRolesForm'

interface AdminRolesSectionProps {
  user: User
  // Super admins may not revoke their own super admin role, which would
  // immediately lock them out of granting it back.
  isSignedInUser: boolean
}

const Role: FC<{ title: string; detail: string }> = ({ title, detail }) => (
  <div className="flex flex-col gap-1 min-w-0">
    <span className="text-sm font-medium text-neutral-900">{title}</span>
    <span className="text-xs text-neutral-600" title={detail}>
      {detail}
    </span>
  </div>
)

// A user holds one role or the other: while somebody is a super admin their
// Time Log Admin facilities are retained but do nothing, so naming them here
// would only be confusing.
const GrantedRoles: FC<{
  superAdmin: boolean
  administeredFacilityNames: string[]
}> = ({ superAdmin, administeredFacilityNames }) => {
  const { t } = useTranslation()

  if (superAdmin) {
    return (
      <Role
        title={t('features.usersAdmin.userDetails.adminRoles.superAdmin')}
        detail={t(
          'features.usersAdmin.userDetails.adminRoles.administersEveryFacility',
        )}
      />
    )
  }

  if (administeredFacilityNames.length > 0) {
    return (
      <Role
        title={t('features.usersAdmin.userDetails.adminRoles.timeLogAdmin')}
        detail={administeredFacilityNames.join(', ')}
      />
    )
  }

  return (
    <span className="text-sm text-neutral-900">
      {t('features.usersAdmin.userDetails.adminRoles.noAdminRoles')}
    </span>
  )
}

export const AdminRolesSection: FC<AdminRolesSectionProps> = ({
  user,
  isSignedInUser,
}) => {
  const { t } = useTranslation()
  const modal = useModal()
  const { data: facilities } = useFacilities()
  const { data: adminRoles } = useUserAdminRoles(user.id)

  const administeredFacilityNames = useMemo(() => {
    if (!adminRoles || !facilities) return []

    return facilities
      .filter(({ id }) => adminRoles.timeLogAdminFacilityIds.includes(id))
      .map(({ name }) => name)
  }, [adminRoles, facilities])

  const edit = () => {
    const id = modal.form({
      title: t(
        'features.usersAdmin.userDetails.adminRoles.editAdminRolesTitle',
        { name: user.name },
      ),
      content: (
        <AdminRolesForm
          user={user}
          isSignedInUser={isSignedInUser}
          onDone={() => modal.close(id)}
        />
      ),
    })
  }

  return (
    <TwoColumn
      label={t('features.usersAdmin.userDetails.adminRoles.adminRoles')}
      description={t(
        'features.usersAdmin.userDetails.adminRoles.adminRolesDescription',
      )}
    >
      <div className="w-[825px]">
        {!adminRoles || !facilities ? (
          <Spinner />
        ) : (
          <div
            data-testid="admin-roles-summary"
            className="flex flex-row items-center justify-between gap-4 w-[400px] min-h-14 px-4 py-3 border rounded-lg bg-neutral-100 border-neutral-200"
          >
            <GrantedRoles
              superAdmin={adminRoles.superAdmin}
              administeredFacilityNames={administeredFacilityNames}
            />
            {/* "Edit" alone is ambiguous on a page that also edits the
                approval group, so the accessible name spells the section out */}
            <button
              type="button"
              aria-label={t(
                'features.usersAdmin.userDetails.adminRoles.editAdminRoles',
              )}
              onClick={edit}
              className="flex items-center gap-2 text-sm font-medium shrink-0 text-primary-600 hover:text-primary-700"
            >
              <FontAwesomeIcon icon={faEdit} />
              {t('common.edit')}
            </button>
          </div>
        )}
      </div>
    </TwoColumn>
  )
}
